import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBackButton from '../common/AppBackButton';
import { Heart, ChatDots, Eye, Star, Bell } from 'react-bootstrap-icons';
import { markAllNotificationsAsRead, markNotificationAsRead } from '../Preview/api';
import { useRealtimeNotificationStore } from '../../store/realtimeNotificationStore';
import {
  buildNotificationCard,
  groupNotificationsByDay,
  matchesNotificationFilter,
  resolveNotificationPath,
  type NotificationFilter,
  type NotificationKind,
} from '../../utils/notificationPageUtils';
import './NotificationsPage.css';

const PROMO_DISMISS_KEY = 'skipscreen_notifications_push_promo_dismissed';

const FILTERS: { key: NotificationFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'messages', label: 'Messages' },
  { key: 'activity', label: 'Activity' },
  { key: 'opportunities', label: 'Opportunities' },
];

function KindIcon({ kind }: { kind: NotificationKind }) {
  const className = `notifications-page__icon notifications-page__icon--${kind}`;
  if (kind === 'like') {
    return (
      <span className={className} aria-hidden>
        <Heart size={22} fill="currentColor" />
      </span>
    );
  }
  if (kind === 'message') {
    return (
      <span className={className} aria-hidden>
        <ChatDots size={22} />
      </span>
    );
  }
  if (kind === 'view') {
    return (
      <span className={className} aria-hidden>
        <Eye size={22} />
      </span>
    );
  }
  if (kind === 'feedback') {
    return (
      <span className={className} aria-hidden>
        <Star size={22} fill="currentColor" />
      </span>
    );
  }
  return (
    <span className={className} aria-hidden>
      <Bell size={20} />
    </span>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const notifications = useRealtimeNotificationStore((s) => s.items);
  const fetchInitial = useRealtimeNotificationStore((s) => s.fetchInitial);
  const markItemReadInStore = useRealtimeNotificationStore((s) => s.markItemRead);
  const setItemsInStore = useRealtimeNotificationStore((s) => s.setItems);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [showPromo, setShowPromo] = useState(() => !localStorage.getItem(PROMO_DISMISS_KEY));

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      await fetchInitial();
    } finally {
      setLoading(false);
    }
  }, [fetchInitial]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  const cards = useMemo(() => {
    return notifications
      .map(buildNotificationCard)
      .filter((card) => matchesNotificationFilter(card.kind, filter));
  }, [notifications, filter]);

  const groups = useMemo(() => groupNotificationsByDay(cards), [cards]);
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return;
    await markAllNotificationsAsRead();
    setItemsInStore(notifications.map((n) => ({ ...n, isRead: true })));
  };

  const openNotification = async (notification: (typeof notifications)[0]) => {
    if (!notification.isRead) {
      await markNotificationAsRead(String(notification.id), {
        message: notification.message,
      });
      markItemReadInStore(String(notification.id));
    }
    const path = resolveNotificationPath(notification);
    if (path.startsWith('http')) {
      window.location.href = path;
      return;
    }
    navigate(path);
  };

  return (
    <div className="notifications-page">
      <div className="notifications-page__toolbar">
        <AppBackButton className="notifications-page__back-btn" />
      </div>

      <header className="notifications-page__head">
        <div>
          <div className="notifications-page__title-row">
            <h1 className="notifications-page__title">Notifications</h1>
            {unreadCount > 0 ? (
              <span className="notifications-page__new-badge">{unreadCount} New</span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="notifications-page__mark-all"
          disabled={unreadCount === 0 || loading}
          onClick={() => void handleMarkAllRead()}
        >
          Mark all as read
        </button>
      </header>

      <div className="notifications-page__filters" role="tablist" aria-label="Filter notifications">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={filter === key}
            className={`notifications-page__filter${
              filter === key ? ' notifications-page__filter--active' : ''
            }`}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="notifications-page__empty">Loading notifications…</p>
      ) : cards.length === 0 ? (
        <p className="notifications-page__empty">No notifications yet.</p>
      ) : (
        groups.map((group) => (
          <section key={group.label} className="notifications-page__section">
            <h2 className="notifications-page__section-label">{group.label}</h2>
            {group.items.map((card) => (
              <article
                key={card.id}
                className={`notifications-page__card${
                  !card.isRead ? ' notifications-page__card--unread' : ''
                }`}
              >
                <span
                  className={`notifications-page__status-dot${
                    card.isRead ? ' notifications-page__status-dot--read' : ''
                  }`}
                  aria-hidden
                />
                <KindIcon kind={card.kind} />
                <div className="notifications-page__body">
                  <p className="notifications-page__card-title">{card.title}</p>
                  {card.subtitle ? (
                    <p className="notifications-page__card-sub">{card.subtitle}</p>
                  ) : null}
                  {card.quote ? (
                    <p className="notifications-page__card-quote">&ldquo;{card.quote}&rdquo;</p>
                  ) : null}
                  <p className="notifications-page__card-time">{card.timeLabel}</p>
                </div>
                {card.actionLabel ? (
                  <button
                    type="button"
                    className="notifications-page__card-action"
                    onClick={() => void openNotification(card.notification)}
                  >
                    {card.actionLabel}
                  </button>
                ) : null}
              </article>
            ))}
          </section>
        ))
      )}

      {showPromo ? (
        <aside className="notifications-page__promo">
          <button
            type="button"
            className="notifications-page__promo-close"
            aria-label="Dismiss"
            onClick={() => {
              localStorage.setItem(PROMO_DISMISS_KEY, '1');
              setShowPromo(false);
            }}
          >
            ×
          </button>
          <div className="notifications-page__promo-art" aria-hidden>
            🔔
          </div>
          <div>
            <h3 className="notifications-page__promo-title">Stay on top of your opportunities</h3>
            <p className="notifications-page__promo-copy">
              Turn on push notifications so you never miss an update.
            </p>
            <button type="button" className="notifications-page__promo-link">
              Enable Notifications
            </button>
          </div>
        </aside>
      ) : null}
    </div>
  );
}
